// Pure helpers of the latency card: averaging call metrics, picking the source, formatting.
// No imports, so scripts/latencyUtils.check.mjs can run it with plain node.

// How far back calls and probes count, and how many calls are averaged.
export const LATENCY_WINDOW_DAYS = 7;
export const LATENCY_CALL_LIMIT = 7;

// One spoken reply of a call (call_logs.metrics is an array of these). Any field may be null.
export interface TurnMetrics {
  stt_eou_delay_ms?: number | null;
  llm_ttft_ms?: number | null;
  llm_buffer_ms?: number | null;
  tts_ttfb_ms?: number | null;
  e2e_latency_ms?: number | null;
}

export interface LatencyBreakdown {
  stt: number | null;
  llm: number | null;
  tts: number | null;
  // The LLM part split, when known.
  llmTtft: number | null;
  llmBuffer: number | null;
  total: number;
}

export type LatencyStage = "stt" | "llm" | "tts";

export interface ProbeRow {
  id: string;
  region: string;
  stt_ms: number | null;
  llm_ms: number | null;
  tts_ms: number | null;
  total_ms: number | null;
  stt_model: string | null;
  llm_model: string | null;
  tts_model: string | null;
  detail: Record<string, any> | null;
  created_at: string;
}

export type LatencySource =
  | { kind: "calls"; breakdown: LatencyBreakdown; callCount: number }
  | {
      kind: "probe";
      breakdown: LatencyBreakdown;
      createdAt: string;
      // Stage -> error text of a stage that failed in the probe.
      errors: Partial<Record<LatencyStage, string>>;
      labels: Record<LatencyStage, string | null>;
    }
  | { kind: "none" };

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

function mean(values: number[]): number | null {
  return values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
}

// Average of one field over the turns of a call, nulls skipped.
function fieldMean(turns: TurnMetrics[], field: keyof TurnMetrics): number | null {
  return mean(turns.map((t) => t?.[field]).filter(isNum));
}

function makeBreakdown(
  stt: number | null,
  llmTtft: number | null,
  llmBuffer: number | null,
  tts: number | null,
): LatencyBreakdown {
  // A buffer without a first-token time says nothing about the LLM.
  const llm = llmTtft === null ? null : llmTtft + (llmBuffer ?? 0);
  return {
    stt,
    llm,
    tts,
    llmTtft,
    llmBuffer,
    total: (stt ?? 0) + (llm ?? 0) + (tts ?? 0),
  };
}

// Each field is averaged over the turns of a call, then over the calls. Calls with no value for
// a field leave it out of that field's average. Returns null when no call has any value.
export function averageCalls(calls: unknown[]): LatencyBreakdown | null {
  const perCall = calls
    .map((m) => (Array.isArray(m) ? (m as TurnMetrics[]) : []))
    .map((turns) => ({
      stt: fieldMean(turns, "stt_eou_delay_ms"),
      ttft: fieldMean(turns, "llm_ttft_ms"),
      buffer: fieldMean(turns, "llm_buffer_ms"),
      tts: fieldMean(turns, "tts_ttfb_ms"),
    }));
  const across = (key: "stt" | "ttft" | "buffer" | "tts") =>
    mean(perCall.map((c) => c[key]).filter(isNum));
  const result = makeBreakdown(across("stt"), across("ttft"), across("buffer"), across("tts"));
  return result.stt === null && result.llm === null && result.tts === null ? null : result;
}

const stageLabel = (s: any): string | null =>
  s?.provider || s?.model ? [s.provider, s.model].filter(Boolean).join(" · ") : null;

// One trial of a stage. LLM trials are consecutive turns of one conversation (`turn`).
export interface ProbeRun {
  trial: number;
  ms: number;
  turn?: number;
  ttft_ms?: number;
  buffer_ms?: number;
  ttfb_ms?: number;
  eou_since_audio_end_ms?: number | null;
}

// `ms` is the median of the trials; `warmup_ms` is trial 1 and `post_warmup_ms` the median of the rest.
export type ProbeStage = { provider?: string; model?: string } & (
  | {
      ms: number;
      error?: undefined;
      runs?: ProbeRun[];
      warmup_ms?: number;
      post_warmup_ms?: number | null;
      failed_trials?: number;
    }
  | { error: string; ms?: undefined }
);

// The probe endpoint's 200 body. id and created_at are null when nothing was saved (every stage failed).
export interface ProbeResponse {
  region: string;
  measured_at: string;
  trials?: number;
  stt: ProbeStage;
  llm: ProbeStage & { ttft_ms?: number; buffer_ms?: number };
  tts: ProbeStage;
  total_ms: number;
  complete: boolean;
  id: string | null;
  created_at: string | null;
}

// The response as a latency_probes row, the shape a stored probe has (detail holds the same JSON),
// so a fresh result and a stored one go through the same pickSource.
export function probeFromResponse(
  body: Partial<ProbeResponse> | any,
  region: string,
  now: number = Date.now(),
): ProbeRow {
  const ms = (s: any) => (s && !s.error && isNum(s.ms) ? s.ms : null);
  return {
    id: body?.id ?? "",
    region,
    stt_ms: ms(body?.stt),
    llm_ms: ms(body?.llm),
    tts_ms: ms(body?.tts),
    total_ms: isNum(body?.total_ms) ? body.total_ms : null,
    stt_model: stageLabel(body?.stt),
    llm_model: stageLabel(body?.llm),
    tts_model: stageLabel(body?.tts),
    // The whole response, as the API server stores it: per-stage runs, warmup_ms, post_warmup_ms, trials.
    detail: { ...body },
    created_at: body?.created_at ?? body?.measured_at ?? new Date(now).toISOString(),
  };
}

function probeBreakdown(p: ProbeRow): LatencyBreakdown {
  const llmDetail = p.detail?.llm ?? {};
  const ttft = isNum(llmDetail.ttft_ms) ? llmDetail.ttft_ms : null;
  const buffer = isNum(llmDetail.buffer_ms) ? llmDetail.buffer_ms : null;
  return {
    stt: p.stt_ms,
    llm: p.llm_ms,
    tts: p.tts_ms,
    llmTtft: ttft,
    llmBuffer: buffer,
    total: (p.stt_ms ?? 0) + (p.llm_ms ?? 0) + (p.tts_ms ?? 0),
  };
}

// Calls first, then the newest probe, else nothing.
export function pickSource(calls: unknown[], probe: ProbeRow | null): LatencySource {
  const fromCalls = averageCalls(calls);
  if (fromCalls) return { kind: "calls", breakdown: fromCalls, callCount: calls.filter((m) => Array.isArray(m) && m.length > 0).length };
  if (probe) {
    const errors: Partial<Record<LatencyStage, string>> = {};
    for (const stage of ["stt", "llm", "tts"] as const) {
      const err = probe.detail?.[stage]?.error;
      if (err) errors[stage] = String(err);
    }
    return {
      kind: "probe",
      breakdown: probeBreakdown(probe),
      createdAt: probe.created_at,
      errors,
      labels: { stt: probe.stt_model, llm: probe.llm_model, tts: probe.tts_model },
    };
  }
  return { kind: "none" };
}

// "~1,440"
export const formatTotal = (ms: number) => `~${Math.round(ms).toLocaleString("en-US")}`;
export const formatMs = (ms: number) => `${Math.round(ms).toLocaleString("en-US")}ms`;

export function formatAgo(iso: string, now: number = Date.now()): string {
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// "avg of last 3 calls" / "probe, 2h ago" (the probe wording reads "probe, just now" at once)
export function sourceLabel(source: LatencySource, now: number = Date.now()): string {
  if (source.kind === "calls") {
    return source.callCount === 1 ? "avg of last call" : `avg of last ${source.callCount} calls`;
  }
  if (source.kind === "probe") return `probe, ${formatAgo(source.createdAt, now)}`;
  return "";
}

// Width of each stage as a share of the total; stages without a value (or failed) get a thin stub.
export function segmentShares(b: LatencyBreakdown): Record<LatencyStage, number> {
  const sum = (b.stt ?? 0) + (b.llm ?? 0) + (b.tts ?? 0);
  if (sum <= 0) return { stt: 1 / 3, llm: 1 / 3, tts: 1 / 3 };
  return { stt: (b.stt ?? 0) / sum, llm: (b.llm ?? 0) / sum, tts: (b.tts ?? 0) / sum };
}
